export interface SiteOffice {
  city: string;
  address: string;
  type: string;
}

export interface SiteConfig {
  key: 'cd' | 'zm';
  siteUrl: string;
  emails: string[];
  phoneNumbers: string[];
  addressLines: [string, string, string];
  footerLocation: {
    primary: string;
    secondary: string;
    tertiary?: string;
  };
  offices: SiteOffice[];
  experienceCenterDescription: string;
  structuredData: {
    addressCountry: string;
    addressLocality: string;
    description: string;
    areaServed: string;
    geo?: {
      latitude: number;
      longitude: number;
    };
  };
}

const SITE_CONFIGS: Record<SiteConfig['key'], SiteConfig> = {
  cd: {
    key: 'cd',
    siteUrl: 'https://coolandcare.cd',
    emails: ['info@coolcare.cd', 'sales@coolcare.cd'],
    phoneNumbers: ['+243 841 364 201', '+243 829 004 038'],
    addressLines: [
      'Immeuble 193, 3eme etage',
      'Unit 301, Boulevard du 30 Juin',
      'Kinshasa, RD Congo',
    ],
    footerLocation: {
      primary: 'Lubumbashi - Kinshasa - Kolwezi',
      secondary: 'Democratic Republic of Congo',
    },
    offices: [
      { city: 'Lubumbashi', type: 'Main Office & Experience Center', address: 'Batiment Infinity (Main Office & Experience Center)' },
      { city: 'Kinshasa', type: 'Regional Office', address: 'Immeuble 1113, 3eme etage, Boulevard de 30 Juin' },
      { city: 'Kolwezi', type: 'Service Center', address: 'Service Center' },
    ],
    experienceCenterDescription: 'Visit our Lubumbashi center to sample and verify products before making a purchase decision.',
    structuredData: {
      addressCountry: 'CD',
      addressLocality: 'Kinshasa',
      description: 'Cool & Care provides IT security, HVAC, fire safety, solar solutions and software development services in the Democratic Republic of Congo.',
      areaServed: 'Democratic Republic of the Congo',
      geo: {
        latitude: -4.4419,
        longitude: 15.2663,
      },
    },
  },
  zm: {
    key: 'zm',
    siteUrl: 'https://coolcare.co.zm',
    emails: ['info@coolcare.co.zm'],
    phoneNumbers: ['+243 841 364 201'],
    addressLines: [
      '802B, Sun Share Tower, 8th Floor',
      'Stand No. LN 15584/1, Katima Mulilo Road, Olympia',
      'Lusaka, Zambia',
    ],
    footerLocation: {
      primary: '802B, Sun Share Tower, 8th Floor',
      secondary: 'Katima Mulilo Road, Olympia',
      tertiary: 'Lusaka, Zambia',
    },
    offices: [
      {
        city: 'Lusaka',
        type: 'Main Office',
        address: '802B, Sun Share Tower, 8th Floor, Stand No. LN 15584/1, Katima Mulilo Road, Olympia, Lusaka, Zambia',
      },
    ],
    experienceCenterDescription: 'Visit our Lusaka office to discuss your project requirements with the Cool & Care team.',
    structuredData: {
      addressCountry: 'ZM',
      addressLocality: 'Lusaka',
      description: 'Cool & Care provides IT security, HVAC, fire safety, solar solutions and software development services in Zambia.',
      areaServed: 'Zambia',
    },
  },
};

const ZM_HOSTNAMES = new Set(['coolcare.co.zm', 'www.coolcare.co.zm']);

const CD_HOSTNAMES = new Set([
  'coolcare.cd',
  'www.coolcare.cd',
  'coolandcare.cd',
  'www.coolandcare.cd',
]);

function getSiteKeyFromHostname(hostname: string): SiteConfig['key'] {
  const normalizedHostname = hostname.toLowerCase();

  if (ZM_HOSTNAMES.has(normalizedHostname)) {
    return 'zm';
  }

  if (
    CD_HOSTNAMES.has(normalizedHostname)
    || normalizedHostname === 'localhost'
    || normalizedHostname === '127.0.0.1'
  ) {
    return 'cd';
  }

  return 'cd';
}

export function isCdSite(hostname?: string): boolean {
  if (typeof window === 'undefined') {
    return true;
  }

  const resolvedHostname = hostname ?? window.location.hostname;
  return getSiteKeyFromHostname(resolvedHostname) === 'cd';
}

export function getSiteConfig(): SiteConfig {
  if (typeof window === 'undefined') {
    return SITE_CONFIGS.cd;
  }

  return SITE_CONFIGS[getSiteKeyFromHostname(window.location.hostname)];
}

export function getSiteUrl(): string {
  const siteConfig = getSiteConfig();

  if (typeof window === 'undefined') {
    return siteConfig.siteUrl;
  }

  const { hostname, origin } = window.location;
  const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';

  return isLocalHost ? siteConfig.siteUrl : origin;
}
